import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { RadioButton } from 'primereact/radiobutton';
import { AssignmentRule, Attribute } from 'services/bidAnalysis/bidAnalysisServices';
import {
  RuleFormState,
  RuleConditionForm,
  FIELD_OPTIONS,
  OPERATOR_OPTIONS,
  CONTRACT_TYPE_OPTIONS,
  updateCondition,
  addCondition,
  removeCondition,
} from '../ReferenceTables.logic';
import classes from '../ReferenceTables.module.scss';

interface RuleModalProps {
  visible: boolean;
  onHide: () => void;
  onSave: () => void;
  editingRule: AssignmentRule | null;
  ruleForm: RuleFormState;
  onFormChange: (form: RuleFormState) => void;
  attributes: Attribute[];
  savingRule: boolean;
}

export const RuleModal = ({
  visible,
  onHide,
  onSave,
  editingRule,
  ruleForm,
  onFormChange,
  attributes,
  savingRule,
}: RuleModalProps) => {
  const handleConditionChange = (index: number, field: keyof RuleConditionForm, value: any) => {
    const newConditions = updateCondition(ruleForm.conditions, index, field, value);
    onFormChange({ ...ruleForm, conditions: newConditions });
  };

  const handleAddCondition = () => {
    const newConditions = addCondition(ruleForm.conditions);
    onFormChange({ ...ruleForm, conditions: newConditions });
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = removeCondition(ruleForm.conditions, index);
    onFormChange({ ...ruleForm, conditions: newConditions });
  };

  return (
    <Dialog
      header={editingRule ? 'Edit Assignment Rule' : 'Create Assignment Rule'}
      visible={visible}
      style={{ width: '800px' }}
      contentStyle={{ maxHeight: '60vh', overflowY: 'auto' }}
      onHide={() => !savingRule && onHide()}
      closable={!savingRule}
      footer={
        <div>
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={onHide}
            className="p-button-text"
            disabled={savingRule}
          />
          <Button
            label={editingRule ? 'Update' : 'Create'}
            icon="pi pi-check"
            onClick={onSave}
            className={classes.primaryButton}
            loading={savingRule}
            disabled={savingRule}
          />
        </div>
      }
    >
      <div className={classes.formContent}>
        {/* Rule Name */}
        <div className={classes.formField}>
          <label htmlFor="rule_name">Rule Name *</label>
          <InputText
            id="rule_name"
            value={ruleForm.rule_name}
            onChange={e => onFormChange({ ...ruleForm, rule_name: e.target.value })}
            placeholder="Enter rule name"
            className={classes.inputField}
            disabled={savingRule}
          />
        </div>

        {/* Contract Type */}
        <div className={classes.formField}>
          <label htmlFor="contract_type">Contract Type *</label>
          <Dropdown
            id="contract_type"
            value={ruleForm.contract_type}
            options={CONTRACT_TYPE_OPTIONS}
            onChange={e => onFormChange({ ...ruleForm, contract_type: e.value })}
            placeholder="Select contract type"
            className={classes.inputField}
            disabled={savingRule}
          />
        </div>

        {/* Combinator */}
        <div className={classes.formField}>
          <label>Combinator *</label>
          <div className={classes.combinatorSelector}>
            <div className="flex align-items-center">
              <RadioButton
                inputId="combinator_and"
                name="combinator"
                value="and"
                onChange={e => onFormChange({ ...ruleForm, combinator: e.value })}
                checked={ruleForm.combinator === 'and'}
                disabled={savingRule}
              />
              <label htmlFor="combinator_and" className="ml-2">
                AND (All conditions must match)
              </label>
            </div>
            <div className="flex align-items-center mt-2">
              <RadioButton
                inputId="combinator_or"
                name="combinator"
                value="or"
                onChange={e => onFormChange({ ...ruleForm, combinator: e.value })}
                checked={ruleForm.combinator === 'or'}
                disabled={savingRule}
              />
              <label htmlFor="combinator_or" className="ml-2">
                OR (Any condition can match)
              </label>
            </div>
          </div>
        </div>

        {/* Conditions */}
        <div className={classes.formField}>
          <label>Conditions *</label>
          <div className={classes.conditionsContainer}>
            {ruleForm.conditions.map((condition, index) => (
              <div key={index} className={classes.ruleConditionRow}>
                {/* Field Dropdown */}
                <Dropdown
                  value={condition.field}
                  options={FIELD_OPTIONS}
                  onChange={e => handleConditionChange(index, 'field', e.value)}
                  placeholder="Select field"
                  className={classes.conditionField}
                  disabled={savingRule}
                />

                {/* Operator Dropdown */}
                <Dropdown
                  value={condition.operator}
                  options={OPERATOR_OPTIONS}
                  onChange={e => handleConditionChange(index, 'operator', e.value)}
                  placeholder="Select operator"
                  className={classes.conditionOperator}
                  disabled={savingRule}
                />

                {/* Value Input(s) */}
                {condition.operator === 'between' ? (
                  <div className={classes.betweenInputs}>
                    <InputNumber
                      value={condition.minValue ? parseFloat(condition.minValue) : null}
                      onValueChange={e =>
                        handleConditionChange(index, 'minValue', e.value?.toString() || '')
                      }
                      placeholder="Min"
                      mode="decimal"
                      minFractionDigits={0}
                      maxFractionDigits={2}
                      className={classes.conditionValue}
                      disabled={savingRule}
                    />
                    <span style={{ margin: '0 0.5rem' }}>-</span>
                    <InputNumber
                      value={condition.maxValue ? parseFloat(condition.maxValue) : null}
                      onValueChange={e =>
                        handleConditionChange(index, 'maxValue', e.value?.toString() || '')
                      }
                      placeholder="Max"
                      mode="decimal"
                      minFractionDigits={0}
                      maxFractionDigits={2}
                      className={classes.conditionValue}
                      disabled={savingRule}
                    />
                  </div>
                ) : (
                  <InputNumber
                    value={condition.value ? parseFloat(condition.value) : null}
                    onValueChange={e =>
                      handleConditionChange(index, 'value', e.value?.toString() || '')
                    }
                    placeholder="Value"
                    mode="decimal"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    className={classes.conditionValue}
                    disabled={savingRule}
                  />
                )}

                {/* Remove Button */}
                <Button
                  icon="pi pi-times"
                  className={`p-button-text p-button-rounded p-button-danger ${classes.removeConditionButton}`}
                  onClick={() => handleRemoveCondition(index)}
                  tooltip="Remove condition"
                  tooltipOptions={{ position: 'top' }}
                  disabled={ruleForm.conditions.length === 1 || savingRule}
                />
              </div>
            ))}

            {/* Add Condition Button */}
            <Button
              label="Add Condition"
              icon="pi pi-plus"
              onClick={handleAddCondition}
              className={`p-button-text ${classes.addConditionButton}`}
              disabled={savingRule}
            />
          </div>
        </div>

        {/* Attributes */}
        <div className={classes.formField}>
          <label htmlFor="attribute_ids">Mapped Attributes *</label>
          <MultiSelect
            id="attribute_ids"
            value={ruleForm.attribute_ids}
            options={attributes.map(attr => ({
              label: attr.attribute_name,
              value: attr.id,
            }))}
            onChange={e => onFormChange({ ...ruleForm, attribute_ids: e.value || [] })}
            placeholder="Select attributes"
            className={`${classes.inputField} ${classes.multiSelectWide}`}
            filter
            display="chip"
            maxSelectedLabels={3}
            selectedItemsLabel="{0} attributes selected"
            disabled={savingRule}
          />
          <small style={{ color: '#6b7280', display: 'block', marginTop: '0.25rem' }}>
            Users with these attributes will be eligible when this rule matches
          </small>
        </div>

        {/* Special Notes */}
        <div className={classes.formField}>
          <label htmlFor="special_notes">Special Notes</label>
          <InputTextarea
            id="special_notes"
            value={ruleForm.special_notes}
            onChange={e => onFormChange({ ...ruleForm, special_notes: e.target.value })}
            placeholder="Enter any special notes or instructions"
            rows={3}
            className={classes.inputField}
            disabled={savingRule}
          />
        </div>
      </div>
    </Dialog>
  );
};

